import { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Convert HTTPS to WSS
const getWebSocketUrl = (path, token) => {
  const baseUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
  return `${baseUrl}${path}?token=${token}`;
};

export function useAdminWebSocket(adminToken) {
  const [isConnected, setIsConnected] = useState(false);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [connectionCount, setConnectionCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const maxEvents = 50; // Keep last 50 events

  const connect = useCallback(() => {
    const readyState = wsRef.current?.readyState;
    if (!adminToken || readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
      return;
    }

    const wsUrl = getWebSocketUrl('/ws/admin', adminToken);
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Admin WebSocket connected');
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Handle different event types
          switch (message.type) {
            case 'connected':
              if (message.data?.online_users) {
                setOnlineUsers(message.data.online_users);
              }
              break;
              
            case 'heartbeat':
              setConnectionCount(message.data?.connections || 0);
              if (message.data?.online_users) {
                setOnlineUsers(message.data.online_users);
              }
              break;

            case 'online_users':
              setOnlineUsers(message.data?.users || []);
              break;
              
            case 'pong':
              // Heartbeat response - ignore
              break;
              
            case 'stats_update':
              setStats(message.data);
              break;
              
            default:
              // Add event to the list
              setEvents(prev => {
                const newEvents = [
                  {
                    id: Date.now(),
                    type: message.type,
                    data: message.data,
                    timestamp: message.timestamp || new Date().toISOString()
                  },
                  ...prev
                ].slice(0, maxEvents);
                return newEvents;
              });
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('Admin WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;
        
        // Reconnect after 5 seconds if not a deliberate close
        if (event.code !== 1000 && adminToken) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('Admin WebSocket error:', error);
      };

    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [adminToken]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Deliberate disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
  }, []);

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send('ping');
    }
  }, []);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  // Connect when token is available
  useEffect(() => {
    if (adminToken) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [adminToken, connect, disconnect]);

  // Send periodic pings to keep connection alive
  useEffect(() => {
    if (!isConnected) return;
    
    const pingInterval = setInterval(() => {
      sendPing();
    }, 30000); // Ping every 30 seconds
    
    return () => clearInterval(pingInterval);
  }, [isConnected, sendPing]);

  return {
    isConnected,
    events,
    stats,
    connectionCount,
    onlineUsers,
    clearEvents,
    reconnect: connect
  };
}

export default useAdminWebSocket;
